import { ShopigurusHTMLElement } from '@shopigurus/base';

/**
 * Complete the Look — PDP CTA that opens the shared <look-variant-drawer>,
 * lets the shopper pick variants/sizes per product, and bulk-adds the
 * selected items to the cart in a single /cart/add.js request.
 *
 * The drawer markup + variant selectors come from snippets/look-variant-drawer.liquid.
 * This component only wires the CTA and the add-to-cart behaviour.
 */
class ShpfltCompleteTheLook extends ShopigurusHTMLElement {
  connectedCallback() {
    super.connectedCallback();

    this.drawer = document.getElementById('LookVariantDrawer');
    this.cta = this.querySelector('[data-ctl-open]');

    if (!this.drawer || !this.cta) return;

    // Move the drawer to <body> so its position:fixed escapes any ancestor
    // stacking context (transforms/filters in the product info column would
    // otherwise trap it below the sticky header).
    if (this.drawer.parentElement !== document.body) {
      document.body.appendChild(this.drawer);
    }

    const { signal } = this.abortController;

    this.cta.addEventListener('click', () => this.#openDrawer(), { signal });

    // Wire the drawer once, even if more than one CTA is present on the page.
    if (!this.drawer.dataset.ctlWired) {
      this.drawer.dataset.ctlWired = 'true';

      const addButton = this.drawer.querySelector('#AddSelectedToCart');
      const closeButton = this.drawer.querySelector('.drawer__close');
      const checkboxes = this.drawer.querySelectorAll('.product-checkbox');

      addButton?.addEventListener('click', () => this.#addSelectedToCart(), { signal });
      closeButton?.addEventListener('click', () => this.#closeDrawer(), { signal });

      // Close when clicking outside the panel (overlay / dimmed area).
      this.drawer.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('.drawer__inner')) this.#closeDrawer();
        },
        { signal }
      );
      checkboxes.forEach((checkbox) =>
        checkbox.addEventListener('change', () => this.#updateSelectedCount(), { signal })
      );

      this.drawer.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') this.#closeDrawer();
        },
        { signal }
      );
    }

    this.#updateSelectedCount();
  }

  #openDrawer() {
    if (!this.drawer) return;
    this.lastFocused = document.activeElement;
    this.drawer.open();
    this.#updateSelectedCount();
  }

  #closeDrawer() {
    if (!this.drawer) return;
    this.drawer.close();
    // Return focus to the CTA that opened the drawer (accessibility).
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    } else {
      this.cta?.focus();
    }
  }

  #updateSelectedCount() {
    if (!this.drawer) return;
    const count = this.drawer.querySelectorAll('.product-checkbox:checked').length;
    const countElement = this.drawer.querySelector('#SelectedCount');
    if (countElement) countElement.textContent = count;

    const addButton = this.drawer.querySelector('#AddSelectedToCart');
    if (addButton) addButton.disabled = count === 0;
  }

  #getProductData(productId) {
    const section = this.drawer.querySelector(`.product-variant-section[data-product-id="${productId}"]`);
    if (!section?.dataset.productData) return null;
    try {
      return JSON.parse(section.dataset.productData);
    } catch (error) {
      return null;
    }
  }

  /**
   * Resolves the selected variant id for a product by matching the values
   * chosen in the drawer's dropdowns / info rows against the product's variants.
   */
  #getSelectedVariantId(productSection, productId) {
    const productData = this.#getProductData(productId);
    if (!productData?.options?.length || !productData?.variants?.length) return null;

    const selectedOptions = [];

    productData.options.forEach((option) => {
      const optionName = typeof option === 'string' ? option : option?.name;
      if (!optionName) return;

      const dropdown = productSection.querySelector(`.variant-dropdown[data-option-name="${optionName}"]`);
      if (dropdown?.value) {
        selectedOptions.push(dropdown.value);
        return;
      }

      const infoElement = productSection.querySelector('.variant-info-value');
      if (infoElement?.textContent) {
        selectedOptions.push(infoElement.textContent.trim());
      }
    });

    return (
      productData.variants.find(
        (variant) =>
          variant.options?.length === selectedOptions.length &&
          variant.options.every((option, index) => option === selectedOptions[index])
      )?.id || null
    );
  }

  async #addSelectedToCart() {
    const addButton = this.drawer.querySelector('#AddSelectedToCart');
    if (!addButton) return;

    const selected = this.drawer.querySelectorAll('.product-checkbox:checked');
    const items = [];

    selected.forEach((checkbox) => {
      const productId = checkbox.getAttribute('data-product-id');
      const productSection = this.drawer.querySelector(`.product-variant-section[data-product-id="${productId}"]`);
      if (!productSection) return;

      const hasDropdowns = productSection.querySelectorAll('.variant-dropdown').length > 0;

      if (hasDropdowns) {
        const variantId = this.#getSelectedVariantId(productSection, productId);
        if (variantId) items.push({ id: variantId, quantity: 1 });
      } else {
        const productData = this.#getProductData(productId);
        const variantId = productData?.selected_or_first_available_variant?.id || productData?.variants?.[0]?.id;
        if (variantId) items.push({ id: variantId, quantity: 1 });
      }
    });

    if (items.length === 0) return;

    this.#clearError();
    this.#setLoading(true);

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js?sections=cart-drawer,cart-icon-bubble`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        // cart/add.js is atomic: if one item fails (e.g. sold out) nothing is
        // added and it returns a localized `description`/`message`. Keep the
        // drawer open and surface the reason.
        this.#showError(result.description || result.message);
        return;
      }

      this.#closeDrawer();

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.renderContents(result);
        cartDrawer.classList.remove('is-empty');
        if (typeof cartDrawer.open === 'function') cartDrawer.open();
      }
    } catch (error) {
      console.error('Complete the Look: error adding products to cart', error);
      this.#showError();
    } finally {
      this.#setLoading(false);
    }
  }

  #showError(message) {
    const el = this.drawer?.querySelector('#LookVariantError');
    if (!el) return;
    el.textContent = message || this.drawer.dataset.errorText || 'Error';
    el.hidden = false;
  }

  #clearError() {
    const el = this.drawer?.querySelector('#LookVariantError');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  #setLoading(isLoading) {
    const addButton = this.drawer.querySelector('#AddSelectedToCart');
    if (!addButton) return;
    addButton.disabled = isLoading;
    addButton.classList.toggle('loading', isLoading);
    addButton.setAttribute('aria-busy', String(isLoading));
  }

  onSectionSelect() {
    this.classList.add('editor-selected');
  }

  onSectionDeselect() {
    this.classList.remove('editor-selected');
  }
}

customElements.define('shpflt-complete-the-look', ShpfltCompleteTheLook);

// The drawer element (open/close) is also defined by the Total Look block on
// its own landing page. Guard so we don't redefine it if both are present.
if (!customElements.get('look-variant-drawer')) {
  customElements.define(
    'look-variant-drawer',
    class LookVariantDrawer extends HTMLElement {
      open() {
        this.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        this.querySelector(
          'button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )?.focus();
      }

      close() {
        this.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    }
  );
}
